import { getImagesFromAttribute } from '../../api/mapping'
import {
    hasRequiredStorefrontFields,
    mergeStorefrontCtx
} from '../../shared/storefront-context'

/**
 * Live Search context integration for headless PLP / search pages.
 *
 * This module bridges the Live Search SDK with the
 * magentoStorefrontEvents SDK (ACDL), using the same data model
 * as the Adobe PLP reference implementation.
 */
const liveSearchContext = {
    /**
     * Logical ID of this search unit.
     * - "plp"   for category / browse pages
     * - "search" for search result pages
     */
    contextInstanceId:
        window.liveSearchConfig?.view === 'catalog' ? 'plp' : 'search',

    /**
     * Convenience accessor for the MSE SDK.
     *
     * @returns {typeof window.magentoStorefrontEvents | null}
     */
    mse() {
        if (typeof window === 'undefined') {
            return null
        }
        return window.magentoStorefrontEvents || null
    },

    /**
     * Called BEFORE the Live Search API is requested.
     *
     * @param {Object}  params
     * @param {string}  params.searchTerm
     * @param {Array}   params.filters
     * @param {Object}  params.sort
     * @param {number}  params.page
     * @param {number}  params.pageSize
     * @param {string}  params.searchRequestId
     * @param {string}  [params.searchUnitId]
     */
    onSearchRequest(
        {
            searchTerm,
            filters,
            sort,
            page,
            pageSize,
            searchRequestId,
            searchUnitId = this.contextInstanceId
        }
    ) {
        const mse = this.mse()
        if (!mse) {
            return
        }

        this.ensureStorefrontInstanceCtx()

        this.updateSearchInputCtx({
            searchUnitId,
            searchRequestId,
            searchTerm,
            filters,
            sort,
            page,
            pageSize
        })

        // Fire standard Live Search event
        mse.publish.searchRequestSent(searchUnitId)
    },

    /**
     * Called AFTER the Live Search API response is received.
     *
     * @param {Object}  params
     * @param {string}  params.searchTerm
     * @param {Array}   params.filters
     * @param {Object}  params.sort
     * @param {number}  params.page
     * @param {number}  params.pageSize
     * @param {Array}   params.items
     * @param {string}  params.searchRequestId
     * @param {string}  [params.searchUnitId]
     */
    onSearchResponse(
        {
            searchTerm,
            filters,
            sort,
            page,
            pageSize,
            items,
            searchRequestId,
            searchUnitId = this.contextInstanceId
        }
    ) {
        const mse = this.mse()
        if (!mse) {
            return
        }

        this.ensureStorefrontInstanceCtx()

        // Keep SearchInputContext in sync with the final parameters
        this.updateSearchInputCtx({
            searchUnitId,
            searchRequestId,
            searchTerm,
            filters,
            sort,
            page,
            pageSize
        })

        // Update SearchResultsContext.units[*] with products etc.
        this.updateSearchResultsCtx({
            searchUnitId,
            searchRequestId,
            items,
            filters,
            page,
            pageSize
        })

        // Fire standard Live Search events
        mse.publish.searchResponseReceived(searchUnitId)

        if (this.view === 'catalog' && typeof mse.publish.categoryResultsView === 'function') {
            mse.publish.categoryResultsView(searchUnitId)
            return
        }

        mse.publish.searchResultsView(searchUnitId)
    },

    /**
     * Called when a product is clicked in the PLP/search results.
     *
     * @param {string} searchUnitId
     * @param {string} sku
     */
    onProductClick(searchUnitId, sku) {
        const mse = this.mse()
        if (!mse) {
            return
        }

        this.ensureStorefrontInstanceCtx()

        mse.publish.searchProductClick(searchUnitId, sku)
    },

    // ------------------------------------------------------------------
    //  Internal helpers – mirror Adobe PLP `events.tsx` behaviour
    // ------------------------------------------------------------------

    /**
     * Ensure storefront instance context exists before publishing events.
     */
    ensureStorefrontInstanceCtx() {
        const mse = this.mse()
        if (!mse || !mse.context?.getStorefrontInstance || !mse.context?.setStorefrontInstance) {
            return
        }

        const currentStorefrontCtx = mse.context.getStorefrontInstance() || {}
        if (hasRequiredStorefrontFields(currentStorefrontCtx)) {
            return
        }

        const fallbackStorefrontCtx = window?.liveSearchConfig?.storefrontInstance || {}
        if (!hasRequiredStorefrontFields(fallbackStorefrontCtx)) {
            return
        }

        mse.context.setStorefrontInstance(
            mergeStorefrontCtx(currentStorefrontCtx, fallbackStorefrontCtx)
        )
    },

    /**
     * Update search input context
     *
     * @param {Object} params
     * @param {string} params.searchUnitId
     * @param {string} params.searchRequestId
     * @param {string} params.searchTerm
     * @param {Array}  params.filters
     * @param {Object} params.sort
     * @param {number} params.page
     * @param {number} params.pageSize
     */
    updateSearchInputCtx(
        {
            searchUnitId,
            searchRequestId,
            searchTerm,
            filters,
            sort,
            page,
            pageSize
        }
    ) {
        const mse = this.mse()
        if (!mse || !mse.context?.getSearchInput || !mse.context?.setSearchInput) {
            return
        }

        const searchInputCtx =
            mse.context.getSearchInput() || { units: [] }

        // Normalize sort to array of { attribute, direction }
        const sortArray = this.buildSearchSort(sort)

        const unit = {
            searchUnitId,
            searchRequestId,
            queryTypes: ['products', 'suggestions'],
            phrase: searchTerm || '',
            pageSize,
            currentPage: page,
            filter: filters || [],
            sort: sortArray
        }

        const index = (searchInputCtx.units || []).findIndex(
            (u) => u.searchUnitId === searchUnitId
        )

        if (index < 0) {
            searchInputCtx.units.push(unit)
        } else {
            searchInputCtx.units[index] = unit
        }

        mse.context.setSearchInput(searchInputCtx)
    },

    /**
     * Update the SearchResultsContext used by Adobe Commerce Live Search.
     *
     * @param {Object} params
     * @param {string} params.searchUnitId
     * @param {string} params.searchRequestId
     * @param {Array}  params.items
     * @param {Array}  params.filters
     * @param {number} params.page
     * @param {number} params.pageSize
     */
    updateSearchResultsCtx(
        {
            searchUnitId,
            searchRequestId,
            items,
            filters,
            page,
            pageSize
        }
    ) {
        const mse = this.mse()
        if (!mse || !mse.context?.getSearchResults || !mse.context?.setSearchResults) {
            return
        }

        const searchResultsCtx =
            mse.context.getSearchResults() || { units: [] }

        const unit = {
            searchUnitId,
            searchRequestId,
            // Map your Live Search items into SearchResultProduct[]
            products: this.buildProducts(items),
            categories: [], // TODO: map categories if applicable
            suggestions: [], // TODO: map suggestions if applicable
            page: page || 1,
            perPage: pageSize || 24,
            facets: this.buildFacets(filters)
        }

        const index = (searchResultsCtx.units || []).findIndex(
            (u) => u.searchUnitId === searchUnitId
        )

        if (index < 0) {
            searchResultsCtx.units.push(unit)
        } else {
            searchResultsCtx.units[index] = unit
        }

        mse.context.setSearchResults(searchResultsCtx)
    },

    /**
     * Normalize Live Search sort definition into the array structure used
     * by the official PLP implementation.
     *
     * @param {Object|Array|undefined} sort
     * @returns {Array}
     */
    buildSearchSort(sort) {
        // If it is already an array in the expected form, just return it
        if (Array.isArray(sort)) {
            return sort
        }

        if (!sort || (!sort.field && !sort.attribute)) {
            return []
        }

        return [{
            attribute: sort.field || sort.attribute,
            direction: sort.direction || 'ASC'
        }]
    },

    /**
     * Map your Live Search items into SearchResultProduct objects.
     *
     * @param {Array} items
     * @returns {Array}
     */
    buildProducts(items) {
        if (!Array.isArray(items)) {
            return []
        }

        return items.map((item, index) => ({
            name: item?.product?.name,
            sku: item?.product?.sku,
            url: item?.productView?.url || item?.product?.canonical_url || '',
            imageUrl: this.getProductImageUrl(item),
            // price shape depends on your GraphQL response – keep it best-effort
            price:
                item?.product?.price_range?.minimum_price?.final_price?.value ??
                item?.product?.price_range?.maximum_price?.final_price?.value,
            rank: index + 1
        }))
    },

    /**
     * Resolve product image URL for SearchResultProduct context.
     *
     * @param {Object} item
     * @returns {string}
     */
    getProductImageUrl(item) {
        const images = getImagesFromAttribute(item)

        return images?.small?.url ||
            images?.large?.url ||
            images?.thumbnail?.url ||
            images?.fallback?.url ||
            ''
    },

    buildFacets(filters) {
        if (!Array.isArray(filters)) {
            return []
        }

        return filters
            .filter((facet) => typeof facet?.attribute === 'string' && facet.attribute.trim() !== '')
            .map((facet) => ({
                attribute: facet.attribute,
                title: facet.title || facet.attribute,
                type: facet.type || 'PINNED',
                buckets: this.buildFacetBuckets(facet.buckets)
            }))
    },

    /**
     * Normalize facet buckets to the Adobe SearchBucket schema.
     *
     * @param {Array|undefined} buckets
     * @returns {Array}
     */
    buildFacetBuckets(buckets) {
        if (!Array.isArray(buckets)) {
            return []
        }

        const toNumber = (value) => {
            const parsed = Number(value)
            return Number.isFinite(parsed) ? parsed : null
        }

        return buckets.map((bucket) => {
            const title = this.getFacetBucketTitle(bucket)
            const baseBucket = {
                title
            }

            const count = toNumber(bucket?.count)
            const from = toNumber(bucket?.from)
            const to = toNumber(bucket?.to)
            const min = toNumber(bucket?.min)
            const max = toNumber(bucket?.max)

            if (count !== null) {
                baseBucket.count = count
            }

            if (from !== null) {
                baseBucket.from = from
            }

            if (to !== null) {
                baseBucket.to = to
            }

            if (min !== null) {
                baseBucket.min = min
            }

            if (max !== null) {
                baseBucket.max = max
            }

            if (baseBucket.count === undefined && baseBucket.min === undefined && baseBucket.max === undefined) {
                baseBucket.count = 0
            }

            return baseBucket
        })
    },

    /**
     * Resolve bucket title from scalar/range/category bucket shapes.
     *
     * @param {Object} bucket
     * @returns {string}
     */
    getFacetBucketTitle(bucket) {
        if (typeof bucket?.title === 'string' && bucket.title.trim() !== '') {
            return bucket.title
        }

        if (typeof bucket?.name === 'string' && bucket.name.trim() !== '') {
            return bucket.name
        }

        if (typeof bucket?.value === 'string' && bucket.value.trim() !== '') {
            return bucket.value
        }

        if (typeof bucket?.path === 'string' && bucket.path.trim() !== '') {
            return bucket.path
        }

        if (bucket?.from !== undefined || bucket?.to !== undefined) {
            const from = bucket?.from ?? ''
            const to = bucket?.to ?? ''
            const rangeLabel = `${from}-${to}`.replace(/^-|-$/g, '').trim()

            if (rangeLabel !== '') {
                return rangeLabel
            }
        }

        if (bucket?.min !== undefined || bucket?.max !== undefined) {
            const min = bucket?.min ?? ''
            const max = bucket?.max ?? ''
            const statsLabel = `${min}-${max}`.replace(/^-|-$/g, '').trim()

            if (statsLabel !== '') {
                return statsLabel
            }
        }

        return 'unknown'
    }
}

export default liveSearchContext
